import type { ToolCall } from '@elara/client-core/models/ToolCallModel';
import type { ModelOption } from '@elara/client-core/states/ModelState';
import type { AgentDebugInfo, DebugSkillInfo, WorkingMemoryDebugInfo } from '@elara/domain/debug-flows';
import type { PromptVersionOption, ReasoningEffort } from '@elara/domain/flows';
import type { ToolCategory } from '@elara/domain/models/ChatMessageModelSpec';
import type { DebugToolDefinition } from '@elara/domain/models/schemas/DebugToolDefinitionSchema';
import type { ToolCallStatus } from '@elara/domain/models/ToolCallSpec';

import type { IconName } from '#ui/index.js';

import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo } from 'react';

import { formatShortToolError } from '@elara/client-core/lib/errors';
import { useStateStore } from '@elara/client-core/lib/signals-utils';
import { cn } from '@elara/client-core/lib/utils';
import { shorten } from '@elara/core/utils';
import { getToolCategoryColor, getToolCategoryIcon } from '@elara/domain/models/ChatMessageModelSpec';

import { useApp, useAppStates } from '#states/hooks.js';
import { UnknownErrorDebugCard } from '#ui/chat/MessageDebugStatusBar.js';
import { renderWidget } from '#ui/chat/widget-renderer.jsx';
import { CollapsibleBlock } from '#ui/debug/collapsible-block.jsx';
import { CollapsibleDump } from '#ui/debug/collapsible-dump.jsx';
import { PrettyTable } from '#ui/debug/pretty.js';
import {
  Badge,
  Card,
  CardContent,
  Loader,
  MaterialIcon,
  Progress,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '#ui/index.js';
import { Select, SelectGroup, SelectItem } from '#ui/select.js';

/** Renders a group of models for a provider */
function ModelGroup({ label, models }: { label: string; models: ModelOption[] | undefined }) {
  if (!models || models.length === 0) return null;
  return (
    <SelectGroup label={label}>
      {models.map((model) => (
        <SelectItem key={model.id} value={model.id}>
          {model.label}
        </SelectItem>
      ))}
    </SelectGroup>
  );
}

const PREFERRED_PROVIDER_ORDER = ['preset', 'google', 'anthropic', 'openai'] as const;

function getProviderLabel(provider: string): string {
  const normalized = provider.trim().toLowerCase();
  if (normalized === 'preset') return 'Presets';
  if (normalized === 'google') return 'Google';
  if (normalized === 'anthropic') return 'Anthropic';
  if (normalized === 'openai') return 'OpenAI';
  return provider
    .split(/[-_/]/)
    .filter((part) => part.length > 0)
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

function parseIsoTimestamp(value: string | undefined): Date | undefined {
  if (!value) return;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return;
  return parsed;
}

function formatLocalDateTime(value: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(value);
}

function formatRelativeTimestamp(value: Date): string {
  return formatDistanceToNow(value, { addSuffix: true });
}

function stripOpenRouterPrefix(model: string | undefined): string | undefined {
  if (!model) return;
  const trimmed = model.trim();
  if (trimmed.length === 0) return;
  if (trimmed.toLowerCase().startsWith('openrouter/')) {
    return trimmed.slice('openrouter/'.length);
  }
  return trimmed;
}

/** Displays the working memory content from Mastra */
/** Check if a value is a primitive (string, number, boolean, null) */
function isPrimitive(value: unknown): value is string | number | boolean | null {
  return value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** Format a primitive value for display */
function formatPrimitiveValue(value: string | number | boolean | null): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') {
    // Truncate very long strings
    return value.length > 100 ? `${value.slice(0, 100)}...` : value;
  }
  return String(value);
}

/** Render a working memory section with smart inline/collapsible display */
function WorkingMemorySection({ label, data }: { label: string; data: unknown }) {
  // For primitives, show inline
  if (isPrimitive(data)) {
    return (
      <div className="flex items-start gap-2 py-1 px-2 rounded bg-violet-50/30 dark:bg-violet-950/20 border border-violet-200/50 dark:border-violet-800/30">
        <span className="text-xs font-medium text-violet-700 dark:text-violet-400 shrink-0">{label}:</span>
        <span className="text-xs text-foreground break-words">{formatPrimitiveValue(data)}</span>
      </div>
    );
  }

  // For objects/arrays, check if all values are primitives (shallow)
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const entries = Object.entries(data as Record<string, unknown>);
    const allPrimitive = entries.every(([, v]) => isPrimitive(v));

    if (allPrimitive && entries.length > 0 && entries.length <= 6) {
      // Render as a compact key-value list
      return (
        <div className="rounded bg-violet-50/30 dark:bg-violet-950/20 border border-violet-200/50 dark:border-violet-800/30 p-2">
          <div className="text-xs font-medium text-violet-700 dark:text-violet-400 mb-1">{label}</div>
          <div className="space-y-0.5">
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-start gap-2 pl-2">
                <span className="text-xs text-muted-foreground shrink-0">{key}:</span>
                <span className="text-xs text-foreground break-words">
                  {formatPrimitiveValue(value as string | number | boolean | null)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  // For complex structures, use collapsible dump
  return (
    <CollapsibleDump
      dump={data}
      label={label}
      icon="memory"
      className="bg-violet-50/30 dark:bg-violet-950/20 border-violet-200/50 dark:border-violet-800/30"
    />
  );
}

function WorkingMemoryCard({
  workingMemory,
  loading,
}: {
  workingMemory: WorkingMemoryDebugInfo | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card className="border-violet-500/50 bg-violet-500/5">
        <CardContent className="py-3 px-3">
          <div className="flex items-center gap-2">
            <Loader loading className="h-4 w-4 text-violet-600" />
            <span className="text-sm text-muted-foreground">Loading working memory...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!workingMemory) {
    return null;
  }

  const hasContent = workingMemory.content && Object.keys(workingMemory.content).length > 0;

  return (
    <Card className="border-violet-500/50 bg-violet-500/5">
      <CardContent className="py-3 px-3">
        <div className="flex items-center gap-2 mb-2">
          <MaterialIcon icon="psychology" className="text-violet-600" />
          <span className="text-sm font-medium">Working Memory</span>
          <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-600">
            {workingMemory.scope === 'thread'
              ? 'chat'
              : workingMemory.scope === 'resource'
                ? 'profile'
                : workingMemory.scope}
          </Badge>
        </div>
        {workingMemory.error && (
          <div className="bg-red-50/50 dark:bg-red-950/20 border border-red-200/50 dark:border-red-800/30 rounded-md p-2 mb-2">
            <div className="text-xs text-red-600 dark:text-red-400">{workingMemory.error}</div>
          </div>
        )}
        {hasContent ? (
          <div className="space-y-2">
            {Object.entries(workingMemory.content!).map(([section, data]) => (
              <WorkingMemorySection key={section} label={section} data={data} />
            ))}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">No working memory content yet.</div>
        )}
        {workingMemory.updatedAt && (
          <div className="text-xs text-muted-foreground mt-2">
            Updated: {formatDistanceToNow(new Date(workingMemory.updatedAt), { addSuffix: true })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatPromptVersion({ version, labels }: PromptVersionOption): string {
  return labels.length > 0 ? `v${version} (${labels.join(', ')})` : `v${version}`;
}

/** Model picker dropdown for privileged users in the debug panel */
function ModelPicker() {
  const { modelState, sessionState } = useAppStates('modelState', 'sessionState');

  useEffect(() => {
    if (sessionState.isPrivileged) {
      void modelState.fetchModels();
      void modelState.fetchPromptVersions();
      void modelState.fetchSkillVersions();
    }
  }, [sessionState.isPrivileged, modelState]);

  const { modelsByProvider, isLoadingModels } = modelState;
  const providerEntries = useMemo(() => {
    return Object.entries(modelsByProvider)
      .filter(([, models]) => Array.isArray(models) && models.length > 0)
      .toSorted(([left], [right]) => {
        const leftRank = PREFERRED_PROVIDER_ORDER.indexOf(left as (typeof PREFERRED_PROVIDER_ORDER)[number]);
        const rightRank = PREFERRED_PROVIDER_ORDER.indexOf(right as (typeof PREFERRED_PROVIDER_ORDER)[number]);
        const leftMissing = leftRank === -1;
        const rightMissing = rightRank === -1;
        if (leftMissing && rightMissing) return left.localeCompare(right);
        if (leftMissing) return 1;
        if (rightMissing) return -1;
        return leftRank - rightRank;
      });
  }, [modelsByProvider]);

  // Hide until data is loaded
  if (!sessionState.isPrivileged || isLoadingModels) return null;

  return (
    <Card className="border-dashed">
      <CardContent className="py-3 px-3">
        <div className="flex items-center gap-2 mb-2">
          <MaterialIcon icon="smart_toy" className="text-muted-foreground" />
          <span className="text-sm font-medium">Model Override</span>
          {isLoadingModels && <Loader loading className="h-3 w-3" />}
        </div>
        <Select
          value={modelState.selectedModel || 'default'}
          onValueChange={(value) => modelState.setSelectedModel(value === 'default' ? '' : value)}
          className="w-full h-9 py-1 text-xs"
        >
          <SelectItem value="default">Agent Default</SelectItem>
          {providerEntries.map(([provider, models]) => (
            <ModelGroup key={provider} label={getProviderLabel(provider)} models={models} />
          ))}
        </Select>
        {modelState.selectedModel && (
          <p className="text-xs text-muted-foreground mt-1">
            Using: <span className="font-mono">{modelState.selectedModel}</span>
          </p>
        )}
        {(modelState.selectedModelOption?.reasoningEfforts?.length ?? 0) > 0 && (
          <>
            <div className="flex items-center gap-2 mt-3 mb-2">
              <MaterialIcon icon="psychology" className="text-muted-foreground" />
              <span className="text-sm font-medium">Reasoning</span>
            </div>
            <Select
              value={modelState.reasoningEffortOverride ?? 'default'}
              onValueChange={(value) =>
                modelState.setSelectedReasoningEffort(value === 'default' ? undefined : (value as ReasoningEffort))
              }
              className="w-full h-9 py-1 text-xs"
            >
              <SelectItem value="default">
                {modelState.selectedModelOption?.defaultReasoningEffort
                  ? `Model default (${modelState.selectedModelOption.defaultReasoningEffort})`
                  : 'Model default'}
              </SelectItem>
              {modelState.selectedModelOption?.reasoningEfforts?.map((effort) => (
                <SelectItem key={effort} value={effort}>
                  {effort}
                </SelectItem>
              ))}
            </Select>
          </>
        )}
        <div className="flex items-center gap-2 mt-3 mb-2">
          <MaterialIcon icon="description" className="text-muted-foreground" />
          <span className="text-sm font-medium">Prompt Version</span>
          {modelState.isLoadingPromptVersions && <Loader loading className="h-3 w-3" />}
        </div>
        <Select
          value={modelState.selectedPromptVersion ? String(modelState.selectedPromptVersion) : 'default'}
          onValueChange={(value) =>
            modelState.setSelectedPromptVersion(value === 'default' ? undefined : Number(value))
          }
          className="w-full h-9 py-1 text-xs"
        >
          <SelectItem value="default">Agent default (production)</SelectItem>
          {modelState.promptVersions.map((option) => (
            <SelectItem key={option.version} value={String(option.version)}>
              {formatPromptVersion(option)}
            </SelectItem>
          ))}
        </Select>
        <SkillVersionsPicker />
      </CardContent>
    </Card>
  );
}

function SkillVersionsPicker() {
  const { modelState } = useAppStates('modelState');
  const { skillVersionOptions, selectedSkillVersions, isLoadingSkillVersions } = modelState;
  const pinnedCount = Object.keys(selectedSkillVersions).length;
  const newer = skillVersionOptions.filter(({ versions }) => {
    const production = versions.find((option) => option.labels.includes('production'))?.version;
    return production !== undefined && versions.some((option) => option.version > production);
  });

  if (skillVersionOptions.length === 0 && !isLoadingSkillVersions) return null;

  return (
    <CollapsibleBlock
      icon="menu_book"
      label={
        <span className="inline-flex items-center gap-2">
          <span className="text-sm font-medium">Skills</span>
          <span className="text-xs text-muted-foreground">
            {pinnedCount ? `${pinnedCount} pinned` : 'all production'}
          </span>
          {isLoadingSkillVersions && <Loader loading className="h-3 w-3" />}
        </span>
      }
      className="mt-3 bg-transparent"
    >
      <div className="flex justify-end gap-3 px-2 pb-1 text-xs">
        <button
          type="button"
          className="text-primary hover:underline disabled:opacity-50"
          disabled={newer.length === 0}
          onClick={() =>
            modelState.setSelectedSkillVersions(
              Object.fromEntries(
                newer.map(({ name, versions }) => [name, versions[0]?.version ?? 0]).filter(([, v]) => v),
              ),
            )
          }
        >
          All latest
        </button>
        <button
          type="button"
          className="text-primary hover:underline disabled:opacity-50"
          disabled={pinnedCount === 0}
          onClick={() => modelState.setSelectedSkillVersions({})}
        >
          All default
        </button>
      </div>
      {skillVersionOptions.map(({ name, versions }) => {
        const production = versions.find((option) => option.labels.includes('production'))?.version;
        const pinned = selectedSkillVersions[name];
        return (
          <div key={name} className="flex items-center justify-between gap-2 px-2 py-0.5">
            <span className="text-xs font-medium">{name}</span>
            <Select
              value={pinned ? String(pinned) : 'default'}
              onValueChange={(value) => {
                const { [name]: _dropped, ...rest } = selectedSkillVersions;
                modelState.setSelectedSkillVersions(value === 'default' ? rest : { ...rest, [name]: Number(value) });
              }}
              className={cn('h-7 py-0 text-xs', pinned && 'border-purple-400 bg-purple-50 dark:bg-purple-950/30')}
            >
              <SelectItem value="default">{production ? `Default (v${production})` : 'Default'}</SelectItem>
              {versions.map((option) => (
                <SelectItem key={option.version} value={String(option.version)}>
                  {formatPromptVersion(option)}
                </SelectItem>
              ))}
            </Select>
          </div>
        );
      })}
    </CollapsibleBlock>
  );
}

function DebugStateTab() {
  const {
    chatsState,
    tripsState,
    sessionState,
    chatInputState,
    messageViewState,
    userProfileState,
    chatDebugState,
    debugInspectorState,
  } = useAppStates(
    'chatsState',
    'tripsState',
    'messageViewState',
    'chatInputState',
    'sessionState',
    'userProfileState',
    'chatDebugState',
    'debugInspectorState',
  );
  const { build } = useApp();
  return (
    <TabsContent value="state" className="flex-1 overflow-hidden px-3">
      <ScrollArea className="h-full">
        <div className="flex flex-col">
          <CollapsibleDump dump={tripsState} label="Trips State" />
          <CollapsibleDump dump={chatDebugState.messages} label="Raw Messages" />
          <CollapsibleDump dump={chatsState.current} label="Current Chat State" />
          <CollapsibleDump dump={sessionState} label="Session State" />
          <CollapsibleDump dump={chatInputState} label="Chat Input State" />
          <CollapsibleDump dump={messageViewState} label="Message View State" />
          <CollapsibleDump dump={userProfileState} label="User Profile State" />
          <CollapsibleDump dump={debugInspectorState} label="Debug Inspector State" />
          <CollapsibleDump dump={chatsState} label="Chats State" />
          <CollapsibleDump dump={build} label="Build State" />
        </div>
      </ScrollArea>
    </TabsContent>
  );
}

export function DebugInspector() {
  const {
    chatsState,
    sessionState,
    debugInspectorState: state,
  } = useAppStates('chatsState', 'sessionState', 'debugInspectorState');
  const { build } = useApp();
  const chatId = chatsState.current?.id;
  const userId = sessionState.user?.uid;

  // Use tool executions from ChatState
  const toolCalls = chatsState.toolCalls;
  const activeToolCalls = chatsState.activeToolCalls;

  // Separate widgets from pure tool calls
  const widgets = toolCalls.filter((tc) => tc.isWidget);
  const pureToolCalls = toolCalls.filter((tc) => !tc.isWidget);
  const toolDescriptionByName = useMemo(
    () => new Map(state.tools.map((tool) => [tool.name, tool.description || 'No description available'] as const)),
    [state.tools],
  );

  // Fetch chat state

  useEffect(() => {
    void state.refresh();
  }, [chatId, userId, state]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs
          value={state.selectedTab}
          onValueChange={(tab) => state.setSelectedTab(tab)}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-5 mx-3 mt-3" style={{ width: 'calc(100% - 1.5rem)' }}>
            <TabsTrigger value="live" className="relative">
              <div className="flex items-baseline gap-1">Live</div>
              {activeToolCalls.length > 0 && (
                <Badge
                  variant={chatsState.hasToolErrors ? 'destructive' : 'info'}
                  className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                >
                  {activeToolCalls.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tools">
              <div className="flex items-baseline gap-1">Tools</div>
            </TabsTrigger>
            <TabsTrigger value="agents" className="relative">
              <div className="flex items-baseline gap-1">Agents</div>
            </TabsTrigger>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="state">State</TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="flex-1 overflow-hidden px-3">
            <ScrollArea className="h-full">
              <div className="space-y-3 pr-3 flex flex-col gap-3">
                <WorkingMemoryCard workingMemory={state.workingMemory} loading={state.workingMemoryLoading} />
                {state.primaryAgent && (
                  <AgentCard
                    agent={state.primaryAgent}
                    expanded={state.expandedAgents.has(state.primaryAgent.id)}
                    onToggle={() => state.toggleAgentExpansion(state.primaryAgent!.id)}
                    toolDescriptionByName={toolDescriptionByName}
                  />
                )}
                <ModelPicker />
                {toolCalls.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <p className="text-sm text-muted-foreground text-center">
                        No tool executions yet.
                        <br />
                        Tools will appear here when they run.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Widgets Section */}
                    {widgets.length > 0 && (
                      <>
                        <div className="text-xs text-muted-foreground">Rendered Widgets ({widgets.length})</div>
                        {widgets.map((widget, idx) => (
                          <WidgetCard key={idx} widget={widget} index={idx} />
                        ))}
                      </>
                    )}

                    {/* Tool Executions Section */}
                    {pureToolCalls.length > 0 && (
                      <>
                        {widgets.length > 0 && (
                          <div className="text-xs text-muted-foreground">Tool Executions ({pureToolCalls.length})</div>
                        )}
                        {pureToolCalls.map((toolCall) => (
                          <ToolExecutionCard key={toolCall.id} toolCall={toolCall} isActive={toolCall.isActive} />
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="tools" className="flex-1 overflow-hidden px-3">
            <ScrollArea className="h-full">
              <div className="space-y-2 pr-3">
                {state.loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading tools...</div>
                ) : state.tools.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No tools available (loaded: {state.tools.length})
                  </div>
                ) : (
                  <ToolsList
                    tools={state.tools}
                    expandedTools={state.expandedTools}
                    onToggle={(name) => state.toggleToolExpansion(name)}
                  />
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="agents" className="flex-1 overflow-hidden px-3">
            <ScrollArea className="h-full">
              <div className="space-y-2 pr-3">
                {state.agentsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading agents...</div>
                ) : state.agents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No agents registered</div>
                ) : (
                  <>
                    <div className="text-xs text-muted-foreground mb-2">{state.agents.length} agents registered</div>
                    {state.agents.map((agent) => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        expanded={state.expandedAgents.has(agent.id)}
                        onToggle={() => state.toggleAgentExpansion(agent.id)}
                        toolDescriptionByName={toolDescriptionByName}
                      />
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="skills" className="flex-1 overflow-hidden px-3">
            <ScrollArea className="h-full">
              <div className="space-y-2 pr-3">
                {state.skillsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading skills...</div>
                ) : state.skillsError ? (
                  <div className="text-center py-8 text-destructive text-sm">{state.skillsError}</div>
                ) : state.skills.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No skills discovered</div>
                ) : (
                  <SkillsList
                    skills={state.skills}
                    uncoveredStates={state.uncoveredStates}
                    expandedSkills={state.expandedSkills}
                    onToggle={(name) => state.toggleSkillExpansion(name)}
                  />
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <DebugStateTab />
        </Tabs>
      </div>
    </div>
  );
}

function SkillsList({
  skills,
  uncoveredStates,
  expandedSkills,
  onToggle,
}: {
  skills: DebugSkillInfo[];
  uncoveredStates: string[];
  expandedSkills: Set<string>;
  onToggle: (name: string) => void;
}) {
  const stateSkills = skills.filter((skill) => skill.states.length > 0);
  const topicSkills = skills.filter((skill) => skill.states.length === 0);
  const coveredCount = stateSkills.reduce((count, skill) => count + skill.states.length, 0);

  const groups: Array<{ label: string; icon: IconName; items: DebugSkillInfo[] }> = [
    { label: 'State skills', icon: 'route', items: stateSkills },
    { label: 'Topic skills', icon: 'sell', items: topicSkills },
  ];

  return (
    <>
      <div className="text-xs text-muted-foreground mb-2">
        {skills.length} {skills.length === 1 ? 'skill' : 'skills'} discovered · {coveredCount}/
        {coveredCount + uncoveredStates.length} states covered
      </div>
      {groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <div key={group.label} className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 rounded-md size-8 flex items-center justify-center bg-muted">
                <MaterialIcon icon={group.icon} className="text-muted-foreground" />
              </div>
              <span className="text-sm font-medium">{group.label}</span>
              <Badge variant="outline" className="text-xs">
                {group.items.length}
              </Badge>
            </div>
            <div className="space-y-1 ml-1">
              {group.items.map((skill) => (
                <SkillCard
                  key={skill.name}
                  skill={skill}
                  expanded={expandedSkills.has(skill.name)}
                  onToggle={() => onToggle(skill.name)}
                />
              ))}
            </div>
          </div>
        ))}
      {uncoveredStates.length > 0 && (
        <CollapsibleDump
          label={`Base prompt states (${uncoveredStates.length})`}
          dump={uncoveredStates}
          icon="info"
        />
      )}
    </>
  );
}

function SkillCard({ skill, expanded, onToggle }: { skill: DebugSkillInfo; expanded: boolean; onToggle: () => void }) {
  return (
    <Card className="overflow-hidden">
      <div className="p-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={onToggle}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate">{skill.name}</span>
              {skill.states.map((stateName) => (
                <Badge key={stateName} variant="secondary" className="text-xs shrink-0">
                  {stateName}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{skill.description}</p>
          </div>
          <div className="shrink-0">
            {expanded ? (
              <MaterialIcon icon="expand_more" className="text-muted-foreground" />
            ) : (
              <MaterialIcon icon="chevron_right" className="text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="flex flex-col gap-1 ml-3">
          <CollapsibleBlock label="Description" icon="input" onCopy={() => skill.description}>
            <p className="text-xs whitespace-pre-wrap">{skill.description}</p>
          </CollapsibleBlock>
          <CollapsibleBlock
            label={`Playbook (${skill.bodyChars.toLocaleString()} chars)`}
            icon="output"
            onCopy={() => skill.instructions}
          >
            <pre className="text-xs whitespace-pre-wrap max-h-96 overflow-auto">{skill.instructions}</pre>
          </CollapsibleBlock>
          <CollapsibleDump
            label="Metadata"
            icon="info"
            dump={{ states: skill.states, tools: skill.tools }}
          />
        </div>
      )}
    </Card>
  );
}

function ExpandedToolBlock({ tool }: { tool: DebugToolDefinition }) {
  return (
    <div className="flex flex-col gap-1 ml-3">
      {tool.inputSchemaDump && (
        <CollapsibleDump label="Input Schema" dump={JSON.parse(tool.inputSchemaDump) as {}} icon={'input'} />
      )}
      {tool.outputSchemaDump && (
        <CollapsibleDump label="Output Schema" dump={JSON.parse(tool.outputSchemaDump) as {}} icon={'output'} />
      )}
      {tool.metadata && <CollapsibleDump label="Metadata" dump={tool.metadata} icon={'info'} />}
    </div>
  );
}

/** Category display order and labels */
const CATEGORY_ORDER = [
  'travel',
  'search',
  'shortlist',
  'itinerary',
  'system',
  'widget',
  'internal',
  'external',
  'agent',
] as const;
const CATEGORY_LABELS: Record<string, string> = {
  system: 'System',
  internal: 'Internal',
  travel: 'Travel',
  search: 'Search',
  external: 'External',
  widget: 'Widgets',
  agent: 'Agents',
  shortlist: 'Shortlist',
  itinerary: 'Itinerary',
};

function ToolsList({
  tools,
  expandedTools,
  onToggle,
}: {
  tools: DebugToolDefinition[];
  expandedTools: Set<string>;
  onToggle: (name: string) => void;
}) {
  const groupedTools = tools.reduce(
    (acc, tool) => {
      const category: ToolCategory = tool.category || 'system';
      if (!acc[category]) acc[category] = [];
      acc[category].push(tool);
      return acc;
    },
    {} as Record<ToolCategory, DebugToolDefinition[]>,
  );

  // eslint-disable-next-line unicorn/no-array-sort
  const sortedCategories = Object.keys(groupedTools).sort((a, b) => {
    const aIndex = CATEGORY_ORDER.indexOf(a as (typeof CATEGORY_ORDER)[number]);
    const bIndex = CATEGORY_ORDER.indexOf(b as (typeof CATEGORY_ORDER)[number]);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  }) as ToolCategory[];

  return (
    <>
      <div className="text-xs text-muted-foreground mb-2">
        {tools.length} tools in {sortedCategories.length} categories
      </div>
      {sortedCategories.map((category) => {
        const categoryTools = groupedTools[category]!;
        return (
          <div key={category} className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div
                className={cn('p-1 rounded-md size-8 flex items-center justify-center', getToolCategoryColor(category))}
              >
                <ToolCategoryIcon category={category} />
              </div>
              <span className="text-sm font-medium capitalize">{CATEGORY_LABELS[category] || category}</span>
              <Badge variant="outline" className="text-xs">
                {categoryTools.length}
              </Badge>
            </div>
            <div className="space-y-1 ml-1">
              {categoryTools.map((tool) => (
                <ToolCard
                  key={tool.name}
                  tool={tool}
                  expanded={expandedTools.has(tool.name)}
                  onToggle={() => onToggle(tool.name)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function ToolCard({
  tool,
  expanded,
  onToggle,
}: {
  tool: DebugToolDefinition;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="p-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={onToggle}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate">{tool.name}</span>
              {!tool.available && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Disabled
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{tool.description}</p>
          </div>
          <div className="shrink-0">
            {expanded ? (
              <MaterialIcon icon="expand_more" className="text-muted-foreground" />
            ) : (
              <MaterialIcon icon="chevron_right" className="text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
      {expanded && <ExpandedToolBlock tool={tool} />}
    </Card>
  );
}

function AgentCard({
  agent,
  expanded,
  onToggle,
  toolDescriptionByName,
}: {
  agent: AgentDebugInfo;
  expanded: boolean;
  onToggle: () => void;
  toolDescriptionByName: ReadonlyMap<string, string>;
}) {
  const promptFetchedAt = parseIsoTimestamp(agent.prompt?.fetchedAt);
  const promptFetchedLabel = promptFetchedAt ? formatRelativeTimestamp(promptFetchedAt) : agent.prompt?.fetchedAt;
  const promptFetchedExactLabel = promptFetchedAt ? formatLocalDateTime(promptFetchedAt) : undefined;
  const promptModelLabel = stripOpenRouterPrefix(agent.model);

  return (
    <Card className={cn('overflow-hidden', expanded && 'bg-muted/50')}>
      <div
        className={cn('p-3 cursor-pointer transition-colors', expanded ? 'bg-muted/50' : 'hover:bg-muted/50')}
        onClick={onToggle}
      >
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <div
                className={cn(
                  'size-5 shrink-0 rounded-sm flex items-center justify-center [&_span]:text-sm',
                  getToolCategoryColor('agent'),
                )}
              >
                <ToolCategoryIcon category="agent" />
              </div>
              <span className="font-medium text-sm truncate">{agent.name}</span>
              {agent.prompt?.version !== undefined && (
                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 shrink-0">
                  v{agent.prompt.version}
                </Badge>
              )}
              {agent.prompt?.labels && agent.prompt.labels.length > 0 && (
                <div className="flex gap-1 shrink-0">
                  {agent.prompt.labels.map((label) => (
                    <Badge key={label} variant="outline" className="text-xs bg-green-500/10 text-green-600">
                      {label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            {agent.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{agent.description}</p>
            )}
            {agent.prompt && (
              <PrettyTable
                data={{
                  Model: promptModelLabel,
                  Fetched: promptFetchedExactLabel ? (
                    <TooltipProvider delayDuration={120}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help">{promptFetchedLabel}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" sideOffset={6}>
                          <p className="text-xs">{promptFetchedExactLabel}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    promptFetchedLabel
                  ),
                  Revision: (
                    <TooltipProvider delayDuration={120}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex min-w-0 items-center gap-2 cursor-help">
                            <span className="shrink-0 font-semibold">v{agent.prompt.version}</span>
                            <span className="min-w-0 truncate">
                              {shorten(agent.prompt.latestChange ?? 'N/A', { maxLength: 112 })}
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" sideOffset={6}>
                          <p className="text-xs">Author: {agent.prompt.latestChangeAuthor?.trim() || 'Unknown'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ),
                  Langfuse: <LangfuseLink promptId={agent.prompt.id} version={agent.prompt.version} />,
                }}
              />
            )}
          </div>
          <div className="shrink-0">
            <MaterialIcon
              icon="chevron_right"
              className={cn('text-muted-foreground transition-transform', expanded && 'rotate-90')}
            />
          </div>
        </div>
      </div>
      {expanded && <ExpandedAgentBlock agent={agent} toolDescriptionByName={toolDescriptionByName} />}
    </Card>
  );
}

const LANGFUSE_PROJECT_URL = 'https://us.cloud.langfuse.com/project/cm7wth4f3004yad07z3mcb7a7/prompts';

function LangfuseLink({ promptId, version }: { promptId: string; version?: string | number | undefined }) {
  const langfuseUrl = `${LANGFUSE_PROJECT_URL}/${encodeURIComponent(promptId)}?version=${version ?? ''}`;
  return (
    <a
      href={langfuseUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:text-primary-hover"
      title="View in Langfuse"
      onClick={(e) => e.stopPropagation()}
    >
      <MaterialIcon icon="open_in_new" size={'xs'} />
    </a>
  );
}

function ExpandedAgentBlock({
  agent,
  toolDescriptionByName,
}: {
  agent: AgentDebugInfo;
  toolDescriptionByName: ReadonlyMap<string, string>;
}) {
  const toolsList = Array.isArray(agent.tools) ? agent.tools : agent.tools === true ? ['(all tools)'] : [];

  return (
    <div className="bg-muted/50 px-3 pb-3 pt-2">
      <div className="rounded-md border border-border/60 bg-background/80 p-2 space-y-2">
        {toolsList.length > 0 && (
          <div className="space-y-1">
            <div className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground">Tools</div>
            <TooltipProvider delayDuration={120}>
              <div className="flex flex-wrap gap-1">
                {toolsList.map((tool, idx) => {
                  const description =
                    tool === '(all tools)'
                      ? 'Uses all registered tools'
                      : (toolDescriptionByName.get(tool) ?? 'No description available');

                  return (
                    <Tooltip key={`${tool}-${idx}`}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Badge variant="outline" className="text-xs cursor-default">
                            {tool}
                          </Badge>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" sideOffset={6} className="max-w-sm">
                        <p className="text-xs">{description}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>
        )}
        {agent.resourceQueries && agent.resourceQueries.length > 0 && (
          <CollapsibleDump
            dump={agent.resourceQueries}
            label={`Resource Queries (${agent.resourceQueries.length})`}
            icon="database"
            className="bg-background/70 border-border/70"
          />
        )}
        {agent.prompt && (
          <CollapsibleDump
            dump={agent.prompt}
            label="Prompt Metadata"
            icon="description"
            className="bg-background/70 border-border/70"
          />
        )}
        {agent.systemPrompt && (
          <CollapsibleDump
            dump={agent.systemPrompt}
            label="System Prompt"
            icon="description"
            className="bg-background/70 border-border/70"
          />
        )}
      </div>
    </div>
  );
}

function WidgetCard({ widget, index }: { widget: ToolCall; index: number }) {
  const state = useStateStore({
    expanded: false,
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
  });

  return (
    <Card className={cn('transition-all duration-200 overflow-hidden', getStatusColor(widget.status))}>
      <div className="p-3 cursor-pointer" onClick={() => state.toggleExpanded()}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <StatusIcon status={widget.extendedStatus} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-sm">{widget.widgetType}</h4>
              <span className="text-xs text-muted-foreground">#{widget.id}</span>
            </div>
            {widget.agent && (
              <p className="text-xs text-muted-foreground mt-1">Agent: {widget.agent.name ?? widget.agent.id}</p>
            )}
            {widget.error && (
              <p className="text-xs text-red-500 mt-1">{formatShortToolError(widget.error, { maxLength: 128 })}</p>
            )}
          </div>
          <MaterialIcon
            icon="chevron_right"
            className={cn('text-muted-foreground transition-transform', state.expanded && 'rotate-90')}
          />
        </div>
      </div>

      {state.expanded && (
        <div className="px-3 py-2 bg-muted/20 space-y-2">
          {widget.toolName === 'showPreferenceProposalWidget' ? (
            // The server builds this card from the tool's output, so show what the model sent and what came back.
            <>
              <CollapsibleDump dump={widget.input} label="Input" icon={'input'} />
              <CollapsibleDump dump={widget.output} label="Output" icon={'output'} />
            </>
          ) : (
            <>
              <CollapsibleDump dump={widget.widgetRawData} label="Props" icon={'table'} />
              {widget.widgetProps?.success ? (
                <CollapsibleBlock label="Rendered Widget" icon={'visibility'}>
                  <div className="mt-2">
                    {renderWidget({ widgetType: widget.widgetType!, data: widget.widgetRawData!, index })}
                  </div>
                </CollapsibleBlock>
              ) : (
                <UnknownErrorDebugCard error={widget.widgetError} />
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function ToolExecutionCard({ toolCall, isActive }: { toolCall: ToolCall; isActive: boolean }) {
  const state = useStateStore({
    expanded: false,
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
  });

  return (
    <Card
      className={cn(
        'transition-all duration-200 overflow-hidden',
        getStatusColor(toolCall.status),
        isActive && 'shadow-lg',
      )}
    >
      <div className="p-4 cursor-pointer" onClick={() => state.toggleExpanded()}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <StatusIcon status={toolCall.extendedStatus} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-sm">{toolCall.toolName}</h4>
              <Badge variant={toolCall.badgeVariant} className="text-xs">
                {toolCall.extendedStatus}
              </Badge>
              {toolCall.durationString && (
                <span className="text-xs text-muted-foreground">{toolCall.durationString}</span>
              )}
            </div>
            {toolCall.error && (
              <p className="text-xs text-red-500 mt-1">{formatShortToolError(toolCall.error, { maxLength: 128 })}</p>
            )}
          </div>
          <MaterialIcon
            icon="chevron_right"
            className={cn('text-muted-foreground transition-transform', state.expanded && 'rotate-90')}
          />
        </div>

        {isActive && (
          <div className="mt-3">
            <Progress value={25} className="h-1" />
          </div>
        )}
      </div>

      {state.expanded && (
        <div className="flex flex-col gap-1 px-4 py-3 bg-muted/20">
          {!!toolCall.input && <CollapsibleDump dump={toolCall.input} label="Input" icon={'input'} />}
          {!!toolCall.output && <CollapsibleDump dump={toolCall.output} label="Output" icon={'output'} />}
          {!!toolCall.error && (
            <CollapsibleDump dump={toolCall.error} label="Error" className="mt-1 text-red-500" icon={'error'} />
          )}

          <PrettyTable
            data={{
              'Started At': toolCall.startedAt,
              'Finished At': toolCall.finishedAt,
              Duration: toolCall.durationString,
            }}
          />

          {toolCall.isStuck && (
            <div className="text-red-500 font-medium">⚠️ This tool call has most likely crashed.</div>
          )}
        </div>
      )}
    </Card>
  );
}

function ToolCategoryIcon({ category }: { category: ToolCategory | undefined | null }) {
  const icon = getToolCategoryIcon(category);
  return <MaterialIcon icon={icon} />;
}

function StatusIcon({ status }: { status: ToolCallStatus | 'stuck' }) {
  switch (status) {
    case 'executing': {
      return <Loader loading className="h-4 w-4 text-blue-500" />;
    }
    case 'completed': {
      return <MaterialIcon icon="check_circle" className="text-green-500" />;
    }
    case 'failed': {
      return <MaterialIcon icon="error" className="text-red-500" />;
    }
    case 'stuck': {
      return <MaterialIcon icon="error" className="text-orange-500" />;
    }
    default: {
      return <MaterialIcon icon="info" className="text-gray-500" />;
    }
  }
}

function getStatusColor(status: ToolCallStatus) {
  switch (status) {
    case 'executing': {
      return 'border-blue-500/50 bg-blue-500/5';
    }
    case 'completed': {
      return 'border-green-500/50 bg-green-500/5';
    }
    case 'failed': {
      return 'border-red-500/50 bg-red-500/5';
    }
    default: {
      return 'border-gray-500/50 bg-gray-500/5';
    }
  }
}
